/**
 * Muhurta D30 Calculation Logic
 * Based on Bhrigu Muni in Manu Samhita - 30 divisions of the day
 *
 * Structure:
 * - 30 Muhurtas - one for each degree (0°-30°)
 * - Each Muhurta = 48 minutes of real time
 * - Formula: Muhurta_number = floor(degrees_in_rashi) + 1
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number;
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * 30 Muhurta types
 */
export const MUHURTA_TYPES = [
  'RAUDRA', 'AHI', 'MAITRA', 'PITRA', 'SAVITRA',
  'VAIRA', 'VISHVA', 'ABHIJIT', 'RAUHINA', 'BALA',
  'VAHI', 'NIRITTA', 'VARUNA', 'ARYAMA', 'BHAGA',
  'GIRISHA', 'AJAPADA', 'AHIRBUDHNYA', 'PUSHA', 'ASHVI',
  'YAMA', 'AGNI', 'VIDHATRA', 'RATRINATHA', 'ADITYA',
  'JIVA', 'VISHNU', 'TAPAS', 'BRAHMA', 'VAYU'
] as const;

export type MuhurtaType = typeof MUHURTA_TYPES[number];

/**
 * Muhurta descriptions with transliteration, meaning, and sphere of influence
 */
export const MUHURTA_DESCRIPTIONS: Record<MuhurtaType, {
  transliteration: string;
  meaning: string;
  sphere: string;
}> = {
  RAUDRA: {
    transliteration: 'Raudra',
    meaning: 'Cry, fear, destructions',
    sphere: 'Changes, transformations'
  },
  AHI: {
    transliteration: 'Ahi',
    meaning: 'Serpents',
    sphere: 'Mysticism, hidden forces'
  },
  MAITRA: {
    transliteration: 'Maitra',
    meaning: 'Friendliness',
    sphere: 'Relations, friendship'
  },
  PITRA: {
    transliteration: 'Pitra',
    meaning: 'Ancestors',
    sphere: 'Ancestral karma'
  },
  SAVITRA: {
    transliteration: 'Savitra',
    meaning: 'Life force',
    sphere: 'Energy, vitality'
  },
  VAIRA: {
    transliteration: 'Vaira',
    meaning: 'Enmity',
    sphere: 'Conflicts, opposition'
  },
  VISHVA: {
    transliteration: 'Vishva',
    meaning: 'Unity',
    sphere: 'Wholeness, unification'
  },
  ABHIJIT: {
    transliteration: 'Abhijit',
    meaning: 'Victory',
    sphere: 'Success, fortune'
  },
  RAUHINA: {
    transliteration: 'Rauhina',
    meaning: 'Sandalwood',
    sphere: 'Fragrance, purification'
  },
  BALA: {
    transliteration: 'Bala',
    meaning: 'Youth',
    sphere: 'Children, youthfulness'
  },
  VAHI: {
    transliteration: 'Vahi',
    meaning: 'Transport',
    sphere: 'Movement, travels'
  },
  NIRITTA: {
    transliteration: 'Niritta',
    meaning: 'Trials',
    sphere: 'Suffering, difficulties'
  },
  VARUNA: {
    transliteration: 'Varuna',
    meaning: 'Water',
    sphere: 'Emotions, purification'
  },
  ARYAMA: {
    transliteration: 'Aryama',
    meaning: 'Partnership',
    sphere: 'Marriage, unions'
  },
  BHAGA: {
    transliteration: 'Bhaga',
    meaning: 'Prosperity',
    sphere: 'Wealth, happiness'
  },
  GIRISHA: {
    transliteration: 'Girisha',
    meaning: 'Mountains',
    sphere: 'Stability, heights'
  },
  AJAPADA: {
    transliteration: 'Ajapada',
    meaning: 'First step',
    sphere: 'Beginnings, leadership'
  },
  AHIRBUDHNYA: {
    transliteration: 'Ahirbudhnya',
    meaning: 'Underworlds',
    sphere: 'Depths, hidden'
  },
  PUSHA: {
    transliteration: 'Pusha',
    meaning: 'Growth',
    sphere: 'Prosperity, development'
  },
  ASHVI: {
    transliteration: 'Ashvi',
    meaning: 'Healing',
    sphere: 'Cure, health'
  },
  YAMA: {
    transliteration: 'Yama',
    meaning: 'Dharma',
    sphere: 'Law, justice'
  },
  AGNI: {
    transliteration: 'Agni',
    meaning: 'Fire',
    sphere: 'Transformation, purification'
  },
  VIDHATRA: {
    transliteration: 'Vidhatra',
    meaning: 'Gifts',
    sphere: 'Distribution of blessings'
  },
  RATRINATHA: {
    transliteration: 'Ratrinatha',
    meaning: 'Night',
    sphere: 'Peace, rest'
  },
  ADITYA: {
    transliteration: 'Aditya',
    meaning: 'Blessings',
    sphere: 'Divine grace'
  },
  JIVA: {
    transliteration: 'Jiva',
    meaning: 'Health',
    sphere: 'Life force'
  },
  VISHNU: {
    transliteration: 'Vishnu',
    meaning: 'Preserver',
    sphere: 'Protection, preservation'
  },
  TAPAS: {
    transliteration: 'Tapas',
    meaning: 'Austerities',
    sphere: 'Spiritual practice'
  },
  BRAHMA: {
    transliteration: 'Brahma',
    meaning: 'Creation',
    sphere: 'Creativity, beginning'
  },
  VAYU: {
    transliteration: 'Vayu',
    meaning: 'Winds',
    sphere: 'Movement, changes'
  }
};

/**
 * Calculate Muhurta number from degrees in rashi (0-30)
 */
export function calculateMuhurtaNumber(degreeInRashi: number): number {
  // Each Muhurta = 1° (one degree)
  // Muhurta_number = floor(degrees) + 1
  const muhurtaIndex = Math.floor(degreeInRashi);
  return Math.min(muhurtaIndex + 1, 30);
}

/**
 * Get Muhurta type from Muhurta number (1-30)
 */
export function getMuhurtaType(muhurtaNumber: number): MuhurtaType {
  return MUHURTA_TYPES[muhurtaNumber - 1] || MUHURTA_TYPES[0];
}

/**
 * Single planet's Muhurta attributes
 */
export interface MuhurtaAttributes {
  planet: PlanetDisplay;
  muhurtaNumber: number;
  muhurtaType: MuhurtaType;
}

/**
 * Complete Muhurta data for all planets
 */
export interface MuhurtaData {
  divisions: MuhurtaAttributes[];
}

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Convert API planet to PlanetDisplay format
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  const longitude = parseLongitudeFromPosition(planet.position || '');

  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  return {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };
}

/**
 * Calculate Muhurta for all planets
 */
export function calculateMuhurtas(apiResponse: JatakaResponse): MuhurtaData {
  const divisions: MuhurtaAttributes[] = [];

  if (!apiResponse.graha_positions?.planets) {
    return { divisions };
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  apiResponse.graha_positions.planets.forEach(planet => {
    // Convert to PlanetDisplay format
    const planetDisplay = convertToPlanetDisplay(planet, currentHoraPlanet);

    // Calculate degree within current rashi (0-30)
    const degreeInRashi = planetDisplay.longitude % 30;

    // Calculate Muhurta number (1-30)
    const muhurtaNumber = calculateMuhurtaNumber(degreeInRashi);

    // Get Muhurta type
    const muhurtaType = getMuhurtaType(muhurtaNumber);

    divisions.push({
      planet: planetDisplay,
      muhurtaNumber,
      muhurtaType
    });
  });

  return { divisions };
}
